import classNames from "classnames";
import React, { forwardRef, useCallback, useLayoutEffect, useRef, useState } from "react";
import PwEyeClosedSvg from "../../../svgs/pw-eye-closed.svg";
import PwEyeSvg from "../../../svgs/pw-eye.svg";
import KuikaSvg from "../kuika-svg/kuika-svg";
import "./kuika-input.scss";

/** Row bounds for a textarea that grows with its content. */
export interface KuikaInputAutoSize {
  minRows: number;
  maxRows: number;
}

export interface KuikaInputProps {
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | any>, value: string | number) => void;
  onPressEnter?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | any>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | any>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | any>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | any>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | any>) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement | any>) => void;
  autoFocus?: boolean;
  value?: string | number;
  defaultValue?: string | number;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftUnit?: string | React.ReactNode;
  rightUnit?: string | React.ReactNode;
  size?: "sm" | "md" | "lg";
  /**
   * Draws `used / maxLength` under the control. Needs `maxLength` to have a budget to
   * count against — a number with nothing to measure it by says less than no number.
   */
  showCount?: boolean;
  className?: string;
  borderless?: boolean;
  type?: "number" | "text" | "password" | "textarea";
  precision?: number;
  readOnly?: boolean;
  /**
   * Draws a clear (x) inside the field while it holds something. The field clears itself and
   * reports the empty value through `onChange`, exactly as if the reader had selected all and
   * deleted; `onClear` is for a caller that has more to undo than the value.
   */
  allowClear?: boolean;
  onClear?: () => void;
  /**
   * Reports a number field's value as the text typed — a string, with every decimal place the
   * reader gave it — the way a native number input and antd's did.
   *
   * By default a number field hands back a number rounded to `precision`, which is 0 unless
   * given, so 9.99 arrives as 10. That suits a count; it corrupts a field that stores what was
   * typed, such as a condition's value that goes on into generated code.
   */
  keepTypedValue?: boolean;
  min?: number;
  max?: number;
  [key: string]: any;
  rows?: number;
  cols?: number;
  resizable?: boolean;
  /**
   * Grows a `type="textarea"` between these row counts as the reader types, then scrolls.
   * A composer that starts at two rows and stops at four reads as one line of chat until it
   * has more to say; a fixed `rows` either wastes the panel or hides what was written.
   */
  autoSize?: KuikaInputAutoSize;
  isAi?: boolean;
  tabIndex?: number;
  /**
   * Draws a show/hide toggle inside a `type="password"` field.
   *
   * Six settings dialogs each held their own `showXPw` boolean and their own `<span onClick>`
   * around the two eye glyphs, and every one of them handed it to antd's `suffix` — a prop
   * this component does not have, so it landed on the `<input>` as `suffix="[object Object]"`
   * and not one of them rendered. The toggle is the field's, not the caller's.
   */
  revealable?: boolean;
  /**
   * Marks the field invalid: red frame and text (`kuika-input--error`). `KuikaFormField` sets it
   * from its rule, and the submit shake (`shakeInvalidFields`) finds invalid fields by this class.
   */
  error?: boolean;
  /**
   * `inverse` draws the field on the canvas selection overlay's surface (its bar and
   * `KuikaPopover tone="inverse"` popovers, --selection-bar-* tokens, themed with the designer).
   * `default` is the regular designer field.
   */
  tone?: "default" | "inverse";
}

/** How many digits a number carries after its decimal point, as written. */
const countDecimalPlaces = (value: number): number => {
  const written: string = String(value);
  const point: number = written.indexOf(".");
  return point < 0 ? 0 : written.length - point - 1;
};

const KuikaInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, KuikaInputProps>(
  (
    {
      disabled,
      placeholder,
      style = {},
      onChange,
      onPressEnter,
      onKeyUp,
      onBlur,
      onKeyDown,
      onFocus,
      onClick,
      autoFocus = false,
      value,
      defaultValue,
      leftIcon,
      rightIcon,
      leftUnit,
      rightUnit,
      size = "md",
      showCount = false,
      className = "",
      borderless = false,
      readOnly = false,
      allowClear = false,
      onClear,
      keepTypedValue = false,
      rows = 3,
      cols = 20,
      type = "text",
      precision = 0,
      min,
      max,
      resizable = true,
      autoSize,
      isAi = false,
      tabIndex,
      error = false,
      revealable = false,
      tone = "default",
      ...rest
    }: KuikaInputProps,
    ref
  ) => {
    const isTextarea = type === "textarea";
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isRevealed, setIsRevealed] = useState<boolean>(false);
    // A revealed field is a text field; the toggle is offered only while there is something
    // to hide and someone able to hide it.
    const canReveal: boolean = revealable && type === "password" && !disabled && !readOnly;
    const resolvedType: KuikaInputProps["type"] = canReveal && isRevealed ? "text" : type;

    // A controlled field's emptiness is the caller's `value`; an uncontrolled one only the
    // field itself knows, so it is remembered as the reader types.
    const [uncontrolledHasContent, setUncontrolledHasContent] = useState<boolean>(
      String(defaultValue ?? "").length > 0
    );
    const hasContent: boolean = value !== undefined ? String(value).length > 0 : uncontrolledHasContent;
    const canClear: boolean = allowClear && !disabled && !readOnly && hasContent;

    const handleClear = (): void => {
      const node: HTMLInputElement | HTMLTextAreaElement | null = isTextarea ? textareaRef.current : inputRef.current;
      if (node) {
        writeValue(node, "");
        node.focus();
      }
      onClear?.();
    };

    const wrapperClassName = classNames("kuika-input", {
      "kuika-input__disabled": disabled,
      "kuika-input__active": !disabled,
      [`kuika-input--${size}`]: size,
      [className]: className,
      "kuika-input--borderless": borderless,
      "kuika-input--textarea": isTextarea,
      "kuika-input--textarea-resizable": isTextarea && resizable,
      "kuika-input--textarea-non-resizable": isTextarea && !resizable,
      // Set while the field CAN be cleared, not only while the clear shows, so the room it needs
      // is held steady from the first render.
      "kuika-input--clearable": allowClear && !disabled && !readOnly,
      "kuika-input--ai": isAi,
      "kuika-input--error": error,
      "kuika-input--inverse": tone === "inverse"
    });

    // `step` is the size of one arrow-key nudge, and `precision` is how many decimal places the
    // field accepts. Writing the place count into `step` asked a field that accepts two decimals
    // to move in twos; the step a decimal place implies is 10 to the minus that many.
    const stepAttribute: number = precision > 0 ? 10 ** -precision : 1;

    // Assigning to `input.value` leaves React's value tracker holding the old string, so the
    // event that follows is dismissed as "nothing changed" and `onChange` never runs — which is
    // why a clamped or arrow-stepped number moved on screen and never reached the caller. Going
    // through the prototype setter updates the tracker, and the event then counts.
    const writeValue = (node: HTMLInputElement | HTMLTextAreaElement, next: string): void => {
      const prototype: object = isTextarea ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
      const setter: ((this: HTMLElement, v: string) => void) | undefined = Object.getOwnPropertyDescriptor(
        prototype,
        "value"
      )?.set;
      if (setter) {
        setter.call(node, next);
      } else {
        node.value = next;
      }
      node.dispatchEvent(new Event("input", { bubbles: true }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let processedValue: string | number = e.target.value;

      if (type === "number" && processedValue !== "" && !keepTypedValue) {
        let numValue = Number(processedValue);

        if (!isNaN(numValue)) {
          if (precision !== undefined) {
            const factor = 10 ** precision;
            numValue = Math.round(numValue * factor) / factor;
          }

          processedValue = numValue;
        }
      }

      Object.defineProperty(e, "target", {
        writable: true,
        value: { ...e.target, value: processedValue }
      });

      setUncontrolledHasContent(String(processedValue).length > 0);
      onChange?.(e, processedValue);
      resizeToContent();
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (type === "number" && e.target.value !== "") {
        let numValue = Number(e.target.value);

        if (!isNaN(numValue)) {
          let shouldUpdate = false;

          if (min !== undefined && numValue < min) {
            numValue = min;
            shouldUpdate = true;
          }
          if (max !== undefined && numValue > max) {
            numValue = max;
            shouldUpdate = true;
          }

          if (shouldUpdate) {
            writeValue(e.target, numValue.toString());
          }
        }
      }

      onBlur?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (type === "number") {
        if (e.key === "e" || e.key === "E" || e.code === "Space") {
          e.preventDefault();
          return;
        }

        if (precision !== undefined && precision > 0 && /\d/.test(e.key)) {
          const input = e.target as HTMLInputElement;
          const currentValue = input.value;

          if (currentValue.includes(".")) {
            const decimalIndex = currentValue.indexOf(".");
            const decimalPlaces = currentValue.length - decimalIndex - 1;

            if (decimalPlaces >= precision) {
              e.preventDefault();
              return;
            }
          }
        }

        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          const input = e.target as HTMLInputElement;
          const currentValue = input.value === "" ? 0 : Number(input.value);

          if (isNaN(currentValue)) {
            return;
          }

          const step = e.shiftKey ? 10 : stepAttribute;
          const stepped = e.key === "ArrowUp" ? currentValue + step : currentValue - step;
          // A step of 0.01 taken in binary lands on 0.30000000000000004; the field's own
          // precision is the number of places it is willing to show, so round to it. A field that
          // keeps what was typed has no such setting, so it keeps the places the value and the
          // step already carry — 9.99 stepped by ten is 19.99, not 20.
          const places: number = keepTypedValue
            ? Math.max(countDecimalPlaces(currentValue), countDecimalPlaces(step))
            : precision;
          const factor = 10 ** places;
          const newValue = Math.round(stepped * factor) / factor;

          if (min !== undefined && newValue < min) {
            e.preventDefault();
            writeValue(input, min.toString());
          } else if (max !== undefined && newValue > max) {
            e.preventDefault();
            writeValue(input, max.toString());
          } else if (e.shiftKey) {
            e.preventDefault();
            writeValue(input, newValue.toString());
          }
        }
      }

      if (e.key === "Enter" && onPressEnter) {
        onPressEnter(e);
      }

      onKeyDown?.(e);
    };

    // The forwarded ref still has to reach the caller; the internal one is what measures, and
    // what the clear button writes through.
    const captureTextarea = (node: HTMLTextAreaElement | null): void => {
      textareaRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      }
    };

    const captureInput = (node: HTMLInputElement | null): void => {
      inputRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    };

    const resizeToContent = useCallback((): void => {
      const node: HTMLTextAreaElement | null = textareaRef.current;
      if (!node || !autoSize) {
        return;
      }
      const styles: CSSStyleDeclaration = window.getComputedStyle(node);
      const lineHeight: number = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5;
      const frame: number =
        parseFloat(styles.paddingTop) +
        parseFloat(styles.paddingBottom) +
        parseFloat(styles.borderTopWidth) +
        parseFloat(styles.borderBottomWidth);
      // Collapse first: scrollHeight only grows, so a shrinking textarea never reports back down.
      node.style.height = "auto";
      const content: number =
        node.scrollHeight + parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
      const floor: number = autoSize.minRows * lineHeight + frame;
      const ceiling: number = autoSize.maxRows * lineHeight + frame;
      node.style.height = `${Math.min(Math.max(content, floor), ceiling)}px`;
      node.style.overflowY = content > ceiling ? "auto" : "hidden";
    }, [autoSize]);

    useLayoutEffect(() => {
      resizeToContent();
    }, [resizeToContent, value, defaultValue]);

    const updatedStyle = {
      ...style,
      ...(leftUnit && { paddingLeft: 0 }),
      ...(rightUnit && { paddingRight: 0 })
    };

    const inputProps = {
      className: "kuika-input__field",
      placeholder,
      disabled,
      value,
      defaultValue,
      onChange: handleChange,
      onKeyUp,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      onFocus,
      onClick,
      autoFocus,
      readOnly,
      tabIndex
    };

    return (
      <div className={wrapperClassName} style={updatedStyle}>
        {leftUnit && <span className="kuika-input__unit-left">{leftUnit}</span>}
        {leftIcon && <span className="kuika-input__icon-left">{leftIcon}</span>}
        {isTextarea ? (
          <textarea
            ref={captureTextarea}
            rows={autoSize ? autoSize.minRows : rows}
            cols={cols}
            {...inputProps}
            {...rest}
          />
        ) : (
          <input
            ref={captureInput}
            type={resolvedType}
            step={stepAttribute}
            min={min}
            max={max}
            {...inputProps}
            {...rest}
          />
        )}
        {canClear && (
          <button type="button" className="kuika-input__clear" aria-label="Clear the value" onClick={handleClear}>
            <KuikaSvg type="common" name="x" width={12} height={12} color="currentColor" />
          </button>
        )}
        {canReveal && (
          <button
            type="button"
            className="kuika-input__reveal"
            aria-label={isRevealed ? "Hide the value" : "Show the value"}
            aria-pressed={isRevealed}
            onClick={(): void => setIsRevealed(!isRevealed)}
          >
            {isRevealed ? <PwEyeSvg /> : <PwEyeClosedSvg />}
          </button>
        )}
        {rightIcon && <span className="kuika-input__icon-right">{rightIcon}</span>}
        {rightUnit && <span className="kuika-input__unit-right">{rightUnit}</span>}
        {showCount && rest.maxLength !== undefined && (
          <span className="kuika-input__count">{`${String(value ?? defaultValue ?? "").length} / ${rest.maxLength}`}</span>
        )}
      </div>
    );
  }
);

export default KuikaInput;
